/**
 * Add — one way in, and it is a list of what you bought.
 *
 * This screen used to offer three ways to record an expense: one category, a
 * manual split across several, or line by line. Three ways to do one thing is
 * not flexibility, it is a question asked before you are allowed to type, and
 * it made the app feel like a form rather than a notebook. There is now one
 * flow, and it is the one that actually answers "where does the money go":
 * name what you bought, and how much of it.
 *
 * Categories still exist and still drive the whole budget — but they are a
 * property of the ITEM, asked once when the item is new. Buy olive oil for the
 * first time and you say where it belongs; buy it again and you say nothing.
 * The splits that carry the money into Needs / Wants / Savings are derived by
 * grouping the lines by their items' categories, and the word "split" never
 * appears on screen.
 *
 * One currency, the household's. No picker, no rate, no conversion.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  Category,
  CreateEntry,
  EntrySplitInput,
  Household,
  Merchant,
  PaymentMeans,
  Product,
} from '@et/contracts';
import type { EntryType } from '@et/core';
import {
  assertSplitsBalance,
  divideRound,
  formatMoney,
  ITEM_UNITS,
  money,
  parseMoney,
  parseQuantity,
  QUANTITY_SCALE,
  type ItemUnit,
  type Money,
} from '@et/core';
import { api, messageOf, type Identity } from './api.js';
import { defaultDateFor } from './months.js';

interface Props {
  identity: Identity;
  household: Household;
  categories: Category[];
  /** The month the app is showing, so a back-filled entry lands in it. */
  month: string;
  products: Product[];
  merchants: Merchant[];
  /** The household's own ways of paying. Managed on the Payment methods screen. */
  means: PaymentMeans[];
  /**
   * Lines to start from, when this was opened by finishing a shop. Names and
   * quantities only — a shopping list knows what you meant to buy, never what
   * it cost, so the prices are still yours to type. Anything the catalogue
   * knows still files itself, so the questions stay at zero.
   */
  draftLines?: Array<{ name: string; quantity: string }>;
  onSaved: () => void;
}

/**
 * One line: a thing, how much of it, and what one of it cost.
 *
 * `categoryId` here is only ever a category the user picked by hand for a name
 * the catalogue does not know. A known item's category is read from the
 * catalogue instead of copied onto the row — copying it would go stale the
 * moment the name is edited, and re-deriving it on every keystroke would make
 * the picker flicker in and out while someone types "Mil… Milk".
 */
interface ItemRow {
  key: string;
  name: string;
  categoryId: string;
  quantity: string;
  unitPrice: string;
  unit: ItemUnit;
}

/** How many suggestions stay scannable. */
const SUGGESTION_COUNT = 8;

const UNIT_LABELS: Record<ItemUnit, string> = {
  PCS: 'pcs',
  KG: 'kg',
  G: 'g',
  L: 'L',
  ML: 'ml',
};

function newRow(): ItemRow {
  return { key: crypto.randomUUID(), name: '', categoryId: '', quantity: '1', unitPrice: '', unit: 'PCS' };
}

/** parseMoney throws on half-typed input, which is normal while someone types. */
function tryParse(value: string, currency: string): Money | null {
  try {
    return parseMoney(value.trim(), currency);
  } catch {
    return null;
  }
}

/**
 * quantity × unit price, in minor units, with no float anywhere near it.
 * 1.5 kg at 2.99 is 4.485, which rounds to 4.49 — half away from zero, the same
 * rule the rest of the app allocates with.
 */
function lineTotalOf(row: ItemRow, currency: string): Money | null {
  const price = tryParse(row.unitPrice, currency);
  // Zero and negative are caught here rather than by the API, so the line that
  // is wrong is the one still on screen.
  if (!price || price.amount <= 0n) return null;
  try {
    return money(divideRound(parseQuantity(row.quantity) * price.amount, QUANTITY_SCALE), currency);
  } catch {
    return null;
  }
}

export function QuickAdd({
  identity,
  household,
  categories,
  month,
  products,
  merchants,
  means,
  draftLines,
  onSaved,
}: Props) {
  const currency = household.baseCurrency;

  const [entryType, setEntryType] = useState<EntryType>('EXPENSE');
  const [rows, setRows] = useState<ItemRow[]>(() =>
    draftLines && draftLines.length > 0
      ? draftLines.map((line) => ({ ...newRow(), name: line.name, quantity: line.quantity }))
      : [newRow()],
  );
  const [merchant, setMerchant] = useState('');
  const [note, setNote] = useState('');
  // Archived means stay on old entries but are not offered again.
  const payable = means.filter((m) => !m.archived);
  const [meansId, setMeansId] = useState('');
  const [spentBy, setSpentBy] = useState(identity.memberId);
  // No date field: the month bar decides. Today when you are on the current
  // month, the 1st when you are back-filling an earlier one.
  const [spentAt, setSpentAt] = useState(() => defaultDateFor(month));
  const [isPrivate, setIsPrivate] = useState(false);
  const [activeLine, setActiveLine] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const firstField = useRef<HTMLInputElement>(null);
  useEffect(() => {
    firstField.current?.focus();
  }, []);

  useEffect(() => {
    setSpentBy(identity.memberId);
  }, [identity.memberId]);

  // Default to the first one the household listed, once the list arrives.
  useEffect(() => {
    setMeansId((current) => (current && payable.some((m) => m.id === current) ? current : payable[0]?.id ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [means]);

  useEffect(() => {
    setSpentAt(defaultDateFor(month));
  }, [month]);

  const isIncome = entryType === 'INCOME';

  /**
   * Income files under one category, always, and nobody is ever asked which.
   *
   * Where money came from is the SOURCE — "Salary", "Freelance" — which is its
   * own list. Categorising it as well would be the same question asked twice.
   * The category still exists because a split must have one; it is plumbing,
   * not a choice (AGENTS.md §3.5d). There is exactly one, so take it.
   */
  const incomeCategoryId = useMemo(
    () => categories.find((c) => c.bucket === 'INCOME' && !c.archived)?.id ?? '',
    [categories],
  );

  // Categories are one level, so every live one is pickable. Income sources and
  // spending categories are different worlds, so only one side is ever offered.
  const choices = useMemo(
    () =>
      categories
        .filter(
          (c) =>
            !c.archived &&
            (entryType === 'INCOME' ? c.bucket === 'INCOME' : c.bucket !== 'INCOME'),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [categories, entryType],
  );

  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  /**
   * Income has its own sources, and they are not things you buy.
   *
   * A product's category decides which world it belongs to: anything filed
   * under an INCOME category is a source of money, everything else is a
   * purchase. That distinction already exists in the data — it just was not
   * being used, so recording a salary offered you Eggs.
   *
   * An UNFILED product (no category yet) appears in both, because there is
   * nothing to place it with and hiding it everywhere would strand it.
   */
  const catalogue = useMemo(
    () =>
      products.filter((p) =>
        p.bucket === null ? true : isIncome ? p.bucket === 'INCOME' : p.bucket !== 'INCOME',
      ),
    [products, isIncome],
  );

  const productsByName = useMemo(
    () => new Map(catalogue.map((p) => [p.name.toLowerCase(), p])),
    [catalogue],
  );

  /**
   * The catalogue in the order worth offering: what you buy most, then what you
   * bought most recently.
   */
  const ranked = useMemo(
    () =>
      [...catalogue].sort(
        (a, b) =>
          b.timesBought - a.timesBought ||
          (b.lastBoughtAt ?? '').localeCompare(a.lastBoughtAt ?? '') ||
          a.name.localeCompare(b.name),
      ),
    [catalogue],
  );

  /**
   * Category names, for the one thing an item may not be called. "Supermarket"
   * is where the money went, not what you got, and an entry that prints both
   * under one word answers two questions with the same label. The API refuses
   * it too — this is so you find out while typing rather than on submit.
   */
  const categoryNames = useMemo(
    () => new Set(categories.filter((c) => !c.archived).map((c) => c.name.toLowerCase())),
    [categories],
  );

  function knownFor(row: ItemRow): Product | undefined {
    return productsByName.get(row.name.trim().toLowerCase());
  }

  /** Only for a name the catalogue does not already hold: an item that exists keeps working. */
  function clashesWithCategory(row: ItemRow): boolean {
    const name = row.name.trim().toLowerCase();
    return name.length > 0 && !productsByName.has(name) && categoryNames.has(name);
  }

  /** The catalogue's answer if it has one, otherwise the one picked by hand. */
  function categoryFor(row: ItemRow): string {
    if (isIncome) return incomeCategoryId;
    return knownFor(row)?.categoryId ?? row.categoryId;
  }

  /** A name the catalogue cannot place is the only thing that earns a picker. */
  function needsCategory(row: ItemRow): boolean {
    if (isIncome) return false;
    return row.name.trim().length > 0 && !knownFor(row)?.categoryId;
  }

  const lineTotals = useMemo(() => rows.map((row) => lineTotalOf(row, currency)), [rows, currency]);

  const total = useMemo(() => {
    let running = 0n;
    let any = false;
    for (const line of lineTotals) {
      if (!line) continue;
      running += line.amount;
      any = true;
    }
    return any ? money(running, currency) : null;
  }, [lineTotals, currency]);

  /**
   * One split per category, carrying the lines that landed in it. A shop that
   * was half groceries and half wine becomes two splits and six items, from one
   * list typed once — and nobody had to know what a split is.
   */
  const splits = useMemo((): EntrySplitInput[] => {
    const groups = new Map<string, EntrySplitInput>();

    rows.forEach((row, index) => {
      const line = lineTotals[index];
      const categoryId = categoryFor(row);
      if (!row.name.trim() || !categoryId || !line) return;

      const known = knownFor(row);
      const item = {
        ...(known ? { productId: known.id } : {}),
        productName: row.name.trim(),
        quantity: row.quantity.trim() || '1',
        unit: row.unit,
        unitPrice: row.unitPrice.trim(),
        lineTotal: formatMoney(line),
      };

      const existing = groups.get(categoryId);
      if (existing) {
        existing.items!.push(item);
        // Recomputed from the group's own lines rather than accumulated as a
        // string, so a split can never drift from what it contains.
        existing.amount = formatMoney(
          money(parseMoney(existing.amount, currency).amount + line.amount, currency),
        );
      } else {
        groups.set(categoryId, { categoryId, amount: formatMoney(line), items: [item] });
      }
    });

    return [...groups.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, lineTotals, currency, productsByName]);

  function updateRow(key: string, patch: Partial<ItemRow>) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  /**
   * Typing a name the catalogue knows fills in its unit and what it cost last
   * time. Both stay editable; neither changes the item.
   */
  function nameChanged(key: string, name: string) {
    const known = productsByName.get(name.trim().toLowerCase());

    setRows((current) =>
      current.map((row) => {
        if (row.key !== key) return row;
        if (!known) return { ...row, name };
        return {
          ...row,
          name,
          unit: known.defaultUnit,
          unitPrice: row.unitPrice || (known.lastUnitPrice ?? ''),
        };
      }),
    );
  }

  function suggestionsFor(row: ItemRow): Product[] {
    const term = row.name.trim().toLowerCase();
    // Once the name IS an item there is nothing left to suggest, and the panel
    // would only cover the fields being moved to.
    if (term && productsByName.has(term)) return [];
    const pool = term ? ranked.filter((p) => p.name.toLowerCase().includes(term)) : ranked;
    return pool.slice(0, SUGGESTION_COUNT);
  }

  /** One tap fills the line: name, unit, last price. */
  function pick(key: string, product: Product) {
    setRows((current) =>
      current.map((row) =>
        row.key === key
          ? {
              ...row,
              name: product.name,
              unit: product.defaultUnit,
              unitPrice: row.unitPrice || (product.lastUnitPrice ?? ''),
            }
          : row,
      ),
    );
    setActiveLine(null);
  }

  const filled = rows.filter((row) => row.name.trim().length > 0);
  const canSave =
    !saving &&
    filled.length > 0 &&
    (isIncome || merchant.trim().length > 0) &&
    total !== null &&
    total.amount > 0n &&
    rows.every((row, index) => {
      // A completely blank row is the one waiting to be typed into, not an error.
      if (!row.name.trim()) return true;
      if (clashesWithCategory(row)) return false;
      return Boolean(categoryFor(row)) && Boolean(lineTotals[index]);
    });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSaved(null);

    if (!total) {
      setError('Add at least one line with a price.');
      return;
    }

    // The same check the API and the database will make, run here so the
    // message arrives before the round trip rather than after it.
    try {
      assertSplitsBalance(
        total,
        splits.map((s) => ({ categoryId: s.categoryId, amount: parseMoney(s.amount, currency) })),
      );
    } catch (e: unknown) {
      setError(messageOf(e));
      return;
    }

    const body: CreateEntry = {
      type: entryType,
      spentAt,
      spentBy,
      merchantName: merchant.trim() || null,
      amount: formatMoney(total),
      paymentMeansId: isIncome ? null : meansId || null,
      paymentLabel: null,
      isPrivate: isIncome ? false : isPrivate,
      source: 'MANUAL',
      note: note.trim() || null,
      splits,
    };

    setSaving(true);
    try {
      const created = await api.createEntry(identity, body);
      setSaved(`${created.amount} ${currency} added`);
      // Keep person, method and date — the next entry is usually like the last.
      setRows([newRow()]);
      setMerchant('');
      setNote('');
      setIsPrivate(false);
      setActiveLine(null);
      firstField.current?.focus();
      onSaved();
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  }


  return (
    <form className="quickadd" onSubmit={submit}>
      <div className="type-switch" role="group" aria-label="Entry type">
        {(['EXPENSE', 'INCOME'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={entryType === value ? 'on' : ''}
            onClick={() => {
              setEntryType(value);
              setRows([newRow()]);
              setActiveLine(null);
            }}
            aria-pressed={entryType === value}
          >
            {value === 'EXPENSE' ? 'Expense' : 'Income'}
          </button>
        ))}
      </div>

      {/* The total is derived and shown as text, not as a disabled input: a
          greyed-out box invites people to try to type in it. */}
      {/* The currency sits INSIDE the field, on the far side of the number.
          It used to be its own grey box to the left, which read as a control
          you could open — and it is not one, there is exactly one currency
          (settings changes it for the whole household). Inside, small and
          muted, it is what it actually is: the unit the figure is in. It is
          not `aria-hidden`: this is a live region, and "EUR 47.32" is a better
          announcement than "47.32". The `{' '}` between the two spans is what
          puts the space in that announcement — flex `gap` is drawn, not read,
          and a white-space-only child of a flex container is not rendered, so
          it costs nothing on screen. */}
      <div className="amount-row">
        <output className="amount-input amount-derived" aria-live="polite" aria-label="Total">
          <span className="amount-currency">{currency}</span>{' '}
          <span className="amount-figure">{total ? formatMoney(total) : '0.00'}</span>
        </output>
      </div>

      <div className="lines">
        {rows.map((row, index) => {
          const line = lineTotals[index];
          const known = knownFor(row);
          const suggestions = activeLine === row.key ? suggestionsFor(row) : [];
          const category = categoryById.get(categoryFor(row));
          const asking = needsCategory(row);

          return (
            <div className="line" key={row.key}>
              <div className="line-top">
                <input
                  ref={index === 0 ? firstField : undefined}
                  className="line-name"
                  autoComplete="off"
                  placeholder={isIncome ? 'Where did it come from?' : 'What did you buy?'}
                  value={row.name}
                  onChange={(e) => nameChanged(row.key, e.target.value)}
                  onFocus={() => setActiveLine(row.key)}
                  // Delayed, so a tap on a suggestion lands before the panel
                  // closes underneath the finger.
                  onBlur={() =>
                    window.setTimeout(
                      () => setActiveLine((current) => (current === row.key ? null : current)),
                      150,
                    )
                  }
                  aria-label="Item"
                />
                {rows.length > 1 && (
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                    aria-label={`Remove ${row.name || 'line'}`}
                  >
                    ×
                  </button>
                )}
              </div>

              {suggestions.length > 0 && (
                <div className="suggest" role="listbox" aria-label="Things you have bought">
                  {suggestions.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="chip suggest-chip"
                      // Keeps focus on the input, so blur never races the click.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(row.key, p)}
                    >
                      {p.bucket && (
                        <span className={`dot dot-${p.bucket.toLowerCase()}`} aria-hidden="true" />
                      )}
                      {p.name}
                      {p.lastUnitPrice && <em>{p.lastUnitPrice}</em>}
                    </button>
                  ))}
                </div>
              )}

              {/* Income is not a basket. Nobody receives two salaries at
                  4000 each; there is a source and there is an amount, so the
                  quantity, the unit and the "×" are three controls asking
                  about something that does not exist. The row keeps
                  quantity "1" underneath so one line total still means
                  1 × price. */}
              <div className="line-bottom">
                {isIncome ? (
                  <input
                    className="line-amount"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={row.unitPrice}
                    onChange={(e) => updateRow(row.key, { unitPrice: e.target.value })}
                    aria-label="Amount"
                  />
                ) : (
                  <>
                    <input
                      className="line-qty"
                      inputMode="decimal"
                      value={row.quantity}
                      onChange={(e) => updateRow(row.key, { quantity: e.target.value })}
                      aria-label="How many"
                    />
                    {/* The unit only matters once you are not counting whole things. */}
                    {row.quantity.trim() !== '1' && (
                      <select
                        className="line-unit"
                        value={row.unit}
                        onChange={(e) => updateRow(row.key, { unit: e.target.value as ItemUnit })}
                        aria-label="Unit"
                      >
                        {ITEM_UNITS.map((unit) => (
                          <option key={unit} value={unit}>
                            {UNIT_LABELS[unit]}
                          </option>
                        ))}
                      </select>
                    )}
                    <span className="line-x" aria-hidden="true">
                      ×
                    </span>
                    <input
                      className="line-price"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={row.unitPrice}
                      onChange={(e) => updateRow(row.key, { unitPrice: e.target.value })}
                      aria-label={row.quantity.trim() === '1' ? 'Price' : 'Price each'}
                    />
                    {line && row.quantity.trim() !== '1' && (
                      <span className="line-total">= {formatMoney(line)}</span>
                    )}
                  </>
                )}
              </div>

              {clashesWithCategory(row) && (
                <p className="line-clash">
                  “{row.name.trim()}” is a category, not something you buy. Choose it below as
                  the category, and give the item its own name.
                </p>
              )}

              {/* The ONE question, and only for a name the catalogue cannot
                  place. Answer it once and this item never asks again. Income
                  never reaches here: a source IS its own classification. */}
              {asking && (
                <label className="line-ask">
                  <span>New item — where does it belong?</span>
                  <select
                    value={row.categoryId}
                    onChange={(e) => updateRow(row.key, { categoryId: e.target.value })}
                    aria-label="Category for this item"
                  >
                    <option value="">Choose…</option>
                    {choices.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {/* Known item: say where it is going, quietly, so the mapping is
                  learned by using the app rather than by reading a settings
                  screen — but do not ask about it. */}
              {!isIncome && !asking && known && category && (
                <p className="line-filed">
                  <span className={`dot dot-${category.bucket.toLowerCase()}`} aria-hidden="true" />
                  {category.name}
                </p>
              )}
            </div>
          );
        })}

        <button type="button" className="chip chip-ghost add-line" onClick={() => setRows((c) => [...c, newRow()])}>
          + Another {isIncome ? 'source' : 'line'}
        </button>

        {catalogue.length === 0 && (
          <p className="hint">
            {isIncome
              ? 'Name the source — “Salary”, “Freelance”, “Rent received”. Nothing else to fill in: the source is the whole answer.'
              : 'Type what you bought — “Eggs”, “Milk”, “Netflix”. You will be asked where each new thing belongs once, and never again.'}
          </p>
        )}
      </div>

      <div className="context">
        <select
          value={spentBy}
          onChange={(e) => setSpentBy(e.target.value)}
          aria-label={isIncome ? 'Who received it' : 'Who spent it'}
        >
          {household.members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.displayName}
            </option>
          ))}
        </select>

        {/* Two expense-shaped controls that income has no answer for.
            "Paid with" is a list of ways to hand money OVER — Card and Direct
            debit are not ways a salary arrives — and "Private" hides a row
            from the rest of the household, which is a thing you might want
            for a birthday present and not for the money the budget divides.
            Income that some members cannot see would make every bucket
            figure depend on who is looking. */}
        {!isIncome && (
          <>
            <select
              value={meansId}
              onChange={(e) => setMeansId(e.target.value)}
              aria-label="Paid with"
            >
              {payable.length === 0 && <option value="">No payment methods yet</option>}
              {payable.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>

            <button
              type="button"
              className={isPrivate ? 'chip chip-on' : 'chip'}
              onClick={() => setIsPrivate((v) => !v)}
              aria-pressed={isPrivate}
            >
              {isPrivate ? 'Private' : 'Shared'}
            </button>
          </>
        )}
      </div>

      <div className="optional">
        <datalist id="known-merchants">
          {merchants.map((m) => (
            <option key={m.id} value={m.name} />
          ))}
        </datalist>
        <input
          list="known-merchants"
          autoComplete="off"
          /* Required on spending, optional on income: money arriving already
             names itself on the line above (AGENTS.md §3.5d). */
          placeholder={isIncome ? 'From whom? (optional)' : 'Where did you buy it?'}
          required={!isIncome}
          value={merchant}
          onChange={(e) => setMerchant(e.target.value)}
          aria-label="Merchant"
        />
        <input
          placeholder="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-label="Note"
        />
      </div>

      {error && <p className="error">{error}</p>}
      {saved && !error && <p className="ok">{saved}</p>}

      <button type="submit" className="primary" disabled={!canSave}>
        {saving ? 'Adding…' : isIncome ? 'Add income' : 'Add expense'}
      </button>
    </form>
  );
}
